/**
 * Human record numbers that stay unique offline (plan §9.4): `<PREFIX>-<DEVICE>-<SEQ>`,
 * e.g. `PB-K7Q-00481`. The device code is assigned by the server, unique per account and
 * never reused; the sequence is a per-device counter. Uniqueness holds by construction.
 * @module
 */

/** Device-code alphabet: no O/0, I/1/L, so codes read back unambiguously. */
export const DEVICE_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
/** Characters in a device code. */
export const DEVICE_CODE_LENGTH = 3;
/** How many distinct device codes exist (31³ = 29 791). */
export const DEVICE_CODE_SPACE = DEVICE_CODE_ALPHABET.length ** DEVICE_CODE_LENGTH;

/**
 * The code in numbers the **server** gives (a batch planned in the Admin: `PB-SRV-00012`).
 * Reserved: {@link allocateDeviceCode} never hands it to a device, so a server number can
 * never collide with a station's.
 */
export const SERVER_DEVICE_CODE = 'SRV';

/** Document-number prefixes. */
export const DOC_PREFIXES = {
  PB: 'production batch',
  GR: 'goods receipt',
  SC: 'stock count',
  L: 'lot',
  PO: 'purchase order',
  PR: 'purchase request',
  WO: 'write-off',
  TR: 'transfer',
  CO: 'customer order',
} as const;
/** A document-number prefix. */
export type DocPrefix = keyof typeof DOC_PREFIXES;

/** Minimum digits in the sequence part (grows beyond without truncation). */
export const SEQ_PAD = 5;

const DEVICE_CODE_RE = new RegExp(`^[${DEVICE_CODE_ALPHABET}]{${DEVICE_CODE_LENGTH}}$`);
const DOC_RE = new RegExp(
  `^(${Object.keys(DOC_PREFIXES).join('|')})-([${DEVICE_CODE_ALPHABET}]{${DEVICE_CODE_LENGTH}})-(\\d{${SEQ_PAD},})$`,
);

/** True for a 3-character code from {@link DEVICE_CODE_ALPHABET} (uppercase only). */
export function isValidDeviceCode(code: string): boolean {
  return DEVICE_CODE_RE.test(code);
}

/** True when `prefix` is a known {@link DocPrefix}. */
export function isDocPrefix(prefix: string): prefix is DocPrefix {
  return Object.hasOwn(DOC_PREFIXES, prefix);
}

function codeAt(index: number): string {
  const n = DEVICE_CODE_ALPHABET.length;
  let out = '';
  for (let i = 0, rest = index; i < DEVICE_CODE_LENGTH; i++, rest = Math.floor(rest / n)) {
    out = DEVICE_CODE_ALPHABET[rest % n] + out;
  }
  return out;
}

/**
 * Picks an unused device code (server side, at provisioning). Never returns a code in
 * `usedCodes` — which must include **every code ever issued** in the account, revoked
 * devices too, so re-provisioning always yields a new code. `rng` returns a float in
 * [0, 1) like `Math.random` (inject a seeded one in tests). Throws when all 29 791 codes
 * are used.
 */
export function allocateDeviceCode(usedCodes: Iterable<string>, rng: () => number): string {
  const used = new Set<string>([SERVER_DEVICE_CODE]);
  for (const c of usedCodes) if (isValidDeviceCode(c)) used.add(c);
  if (used.size >= DEVICE_CODE_SPACE) throw new Error('All device codes are used');
  const r = rng();
  if (!(r >= 0 && r < 1)) throw new RangeError('rng must return a number in [0, 1)');
  const start = Math.floor(r * DEVICE_CODE_SPACE);
  // Probe forward from a random start: always terminates, since a free code exists.
  for (let i = 0; ; i++) {
    const code = codeAt((start + i) % DEVICE_CODE_SPACE);
    if (!used.has(code)) return code;
  }
}

/** The parts of a document number. */
export interface DocNumber {
  prefix: DocPrefix;
  deviceCode: string;
  seq: number;
}

/**
 * Formats a document number: `formatDocNumber('PB', 'K7Q', 481)` → `PB-K7Q-00481`.
 * `seq` is an integer ≥ 1; it is padded to 5 digits and grows past 99999 untruncated.
 */
export function formatDocNumber(prefix: DocPrefix, deviceCode: string, seq: number): string {
  if (!isDocPrefix(prefix)) throw new RangeError(`Unknown document prefix: ${prefix}`);
  if (!isValidDeviceCode(deviceCode)) throw new RangeError(`Invalid device code: ${deviceCode}`);
  if (!Number.isSafeInteger(seq) || seq < 1) {
    throw new RangeError(`Sequence must be an integer >= 1, got ${seq}`);
  }
  return `${prefix}-${deviceCode}-${String(seq).padStart(SEQ_PAD, '0')}`;
}

/**
 * Parses a document number (typed or scanned; case-insensitive, trimmed). Returns `null`
 * unless it is exactly what {@link formatDocNumber} would produce.
 */
export function parseDocNumber(text: string): DocNumber | null {
  const m = DOC_RE.exec(text.trim().toUpperCase());
  if (!m) return null;
  const digits = m[3] as string;
  // Only the canonical form: no extra leading zeros beyond the 5-digit pad.
  if (digits.length > SEQ_PAD && digits.startsWith('0')) return null;
  const seq = Number(digits);
  if (seq < 1 || !Number.isSafeInteger(seq)) return null;
  return { prefix: m[1] as DocPrefix, deviceCode: m[2] as string, seq };
}
